export default {
  // Default variant
  //-------------------------------------------------------------------------
  default: {
    // Layout & Containers
    container: {
      wrapper: "relative",
      modal: "fixed inset-0 bg-theme_black/50 flex items-center justify-center z-50",
      mainContainer:
        "bg-admin_paper rounded-[28px] max-w-[360px] w-full shadow-xl relative",
      header: "px-6 pt-6",
      body: "px-6 pb-6",
      footer: "flex items-center justify-between p-4 border-t border-text/10",
    },

    // Input Display
    input: {
      wrapper:
        "cursor-pointer p-4 bg-admin_paper rounded-lg border border-admin_bordered",
      label: "text-sm text-admin_text/60 mb-1",
      value: "text-sm text-admin_text",
    },

    // Header Elements
    header: {
      title: "text-admin_text text-base font-normal",
      closeButton: "w-6 h-6 text-admin_text/60 cursor-pointer",
      selectedDate: "text-[25px] text-admin_text font-normal mb-4 text-center",
      rangeRow: "flex items-stretch gap-3 mb-4",
      rangeSlot: "flex-1 min-w-0 rounded-xl px-3 py-2 border border-admin_bordered text-left",
      rangeSlotActive: "border-primary bg-primary/5",
      rangeLabel: "text-[11px] uppercase tracking-wide text-admin_text/50",
      rangeValue: "text-[17px] leading-tight text-admin_text font-normal",
      monthRow: "flex items-center justify-center gap-1 mb-4",
      monthStep:
        "w-9 h-9 rounded-full hover:bg-text/10 flex items-center justify-center text-admin_text",
      monthSelector:
        "text-admin_text hover:bg-text/10 px-4 py-2 rounded-lg flex items-center",
    },

    // Calendar Grid
    calendar: {
      weekdayHeader: "text-center text-sm py-2 text-admin_text/60",
      daysGrid: "grid grid-cols-7",
      dayButton: {
        base: "relative aspect-square flex items-center justify-center text-sm text-admin_text rounded-full hover:bg-primary",
        selected: "text-text_inv bg-primary",
        today: "font-bold text-primary",
        otherMonth: "text-admin_text/40",
        inRange: "bg-primary/20",
        dayWrapper: "w-10 h-10 flex items-center justify-center rounded-full",
      },
    },

    // Month/Year Selector
    monthSelector: {
      container: "bg-admin_paper rounded-[28px] p-6",
      header: {
        wrapper: "flex items-center justify-between mb-6",
        format: "text-admin_text/60",
        closeButton: "w-6 h-6 text-admin_text/60 cursor-pointer",
      },
      selectors: {
        wrapper: "flex gap-4 mb-6",
        button: {
          base: "w-full flex items-center justify-between gap-2 px-4 py-2 rounded-lg border border-admin_bordered",
          active: "bg-text/10",
          hover: "hover:bg-text/5",
          disabled: "text-admin_text/40",
        },
      },
      monthList: {
        wrapper: "space-y-2",
        item: {
          base: "w-full px-4 py-3 rounded-lg text-left flex items-center",
          selected: "bg-primary/10",
          hover: "hover:bg-text/5",
          checkmark: "w-5 h-5 text-primary mr-2",
        },
      },
    },

    // Footer Buttons
    buttons: {
      base: "px-6 py-2 rounded-full",
      primary: "text-primary hover:bg-primary/10",
      secondary: "text-primary hover:bg-text/10",
    },
  },

  // Compact variant
  //-------------------------------------------------------------------------
  compact: {
    // Layout & Containers
    container: {
      wrapper: "relative",
      modal: "fixed inset-0 bg-theme_black/50 flex items-center justify-center z-50",
      mainContainer:
        "bg-admin_paper rounded-[20px] max-w-[320px] w-full shadow-lg relative",
      header: "px-4 pt-4",
      body: "px-4 pb-4",
      footer: "flex items-center justify-between p-3 border-t border-text/10",
    },

    // Input Display
    input: {
      wrapper:
        "cursor-pointer p-3 bg-admin_paper rounded-lg border border-admin_bordered",
      label: "text-xs text-admin_text/60 mb-0.5",
      value: "text-xs text-admin_text",
    },

    // Header Elements
    header: {
      title: "text-admin_text text-sm font-normal",
      closeButton: "w-5 h-5 text-admin_text/60 cursor-pointer",
      selectedDate: "text-2xl text-admin_text font-normal mb-3",
      rangeRow: "flex items-stretch gap-2 mb-3",
      rangeSlot: "flex-1 min-w-0 rounded-lg px-2 py-1.5 border border-admin_bordered text-left",
      rangeSlotActive: "border-primary bg-primary/5",
      rangeLabel: "text-[10px] uppercase tracking-wide text-admin_text/50",
      rangeValue: "text-[13px] leading-tight text-admin_text font-normal",
      monthRow: "flex items-center justify-center gap-1 mb-3",
      monthStep:
        "w-8 h-8 rounded-full hover:bg-text/10 flex items-center justify-center text-admin_text",
      monthSelector:
        "text-admin_text hover:bg-text/10 px-3 py-1.5 rounded-lg flex items-center text-sm",
    },

    // Calendar Grid
    calendar: {
      weekdayHeader: "text-center text-xs py-1.5 text-admin_text/60",
      daysGrid: "grid grid-cols-7",
      dayButton: {
        base: "relative aspect-square flex items-center justify-center text-xs text-admin_text rounded-full hover:bg-primary",
        selected: "text-text_inv bg-primary",
        today: "font-bold text-primary",
        otherMonth: "text-admin_text/40",
        inRange: "bg-primary/20",
        dayWrapper: "w-8 h-8 flex items-center justify-center rounded-full",
      },
    },

    // Month/Year Selector
    monthSelector: {
      container: "bg-admin_paper rounded-[28px] p-4",
      header: {
        wrapper: "flex items-center justify-between mb-4",
        format: "text-xs text-admin_text/60",
        closeButton: "w-5 h-5 text-admin_text/60 cursor-pointer",
      },
      selectors: {
        wrapper: "flex gap-3 mb-4",
        button: {
          base: "w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border border-admin_bordered text-sm",
          active: "bg-text/10",
          hover: "hover:bg-text/5",
          disabled: "text-admin_text/40",
        },
      },
      monthList: {
        wrapper: "space-y-1",
        item: {
          base: "w-full px-3 py-2 rounded-lg text-left flex items-center text-sm",
          selected: "bg-primary/10",
          hover: "hover:bg-text/5",
          checkmark: "w-4 h-4 text-primary mr-1.5",
        },
      },
    },

    // Footer Buttons
    buttons: {
      base: "px-4 py-1.5 rounded-full text-sm",
      primary: "text-primary hover:bg-primary/10",
      secondary: "text-primary hover:bg-text/10",
    },
  },

  // Large variant
  //-------------------------------------------------------------------------
  large: {
    // Layout & Containers
    container: {
      wrapper: "relative",
      modal: "fixed inset-0 bg-theme_black/50 flex items-center justify-center z-50",
      mainContainer:
        "bg-admin_paper rounded-[32px] max-w-[420px] w-full shadow-2xl relative",
      header: "px-8 pt-8",
      body: "px-8 pb-8",
      footer: "flex items-center justify-between p-5 border-t border-text/10",
    },

    // Input Display
    input: {
      wrapper:
        "cursor-pointer p-5 bg-admin_paper rounded-lg border border-admin_bordered",
      label: "text-base text-admin_text/60 mb-1.5",
      value: "text-base text-admin_text",
    },

    // Header Elements
    header: {
      title: "text-admin_text text-lg font-normal",
      closeButton: "w-7 h-7 text-admin_text/60 cursor-pointer",
      selectedDate: "text-4xl text-admin_text font-normal mb-5",
      rangeRow: "flex items-stretch gap-4 mb-5",
      rangeSlot: "flex-1 min-w-0 rounded-2xl px-4 py-3 border border-admin_bordered text-left",
      rangeSlotActive: "border-primary bg-primary/5",
      rangeLabel: "text-xs uppercase tracking-wide text-admin_text/50",
      rangeValue: "text-[20px] leading-tight text-admin_text font-normal",
      monthRow: "flex items-center justify-center gap-2 mb-5",
      monthStep:
        "w-10 h-10 rounded-full hover:bg-text/10 flex items-center justify-center text-admin_text",
      monthSelector:
        "text-admin_text hover:bg-text/10 px-5 py-2.5 rounded-lg flex items-center text-lg",
    },

    // Calendar Grid
    calendar: {
      weekdayHeader: "text-center text-base py-2.5 text-admin_text/60",
      daysGrid: "grid grid-cols-7",
      dayButton: {
        base: "relative aspect-square flex items-center justify-center text-base text-admin_text rounded-full hover:bg-primary",
        selected: "text-text_inv bg-primary",
        today: "font-bold text-primary",
        otherMonth: "text-admin_text/40",
        inRange: "bg-primary/20",
        dayWrapper: "w-12 h-12 flex items-center justify-center rounded-full",
      },
    },

    // Month/Year Selector
    monthSelector: {
      container: "bg-admin_paper rounded-[28px] p-8",
      header: {
        wrapper: "flex items-center justify-between mb-6",
        format: "text-base text-admin_text/60",
        closeButton: "w-7 h-7 text-admin_text/60 cursor-pointer",
      },
      selectors: {
        wrapper: "flex gap-5 mb-6",
        button: {
          base: "w-full flex items-center justify-between gap-2 px-5 py-2.5 rounded-lg border border-admin_bordered text-lg",
          active: "bg-text/10",
          hover: "hover:bg-text/5",
          disabled: "text-admin_text/40",
        },
      },
      monthList: {
        wrapper: "space-y-2.5",
        item: {
          base: "w-full px-5 py-3.5 rounded-lg text-left flex items-center text-lg",
          selected: "bg-primary/10",
          hover: "hover:bg-text/5",
          checkmark: "w-6 h-6 text-primary mr-2.5",
        },
      },
    },

    // Footer Buttons
    buttons: {
      base: "px-8 py-2.5 rounded-full text-lg",
      primary: "text-primary hover:bg-primary/10",
      secondary: "text-primary hover:bg-text/10",
    },
  },

  // Admin variant
  //-------------------------------------------------------------------------
  admin: {
    // Layout & Containers
    container: {
      wrapper: "relative",
      modal: "fixed inset-0 bg-admin_black/50 flex items-center justify-center z-50",
      mainContainer:
        "bg-admin_paper rounded-[28px] max-w-[360px] w-full shadow-xl relative border border-admin_neutral_1",
      header: "px-6 pt-6",
      body: "px-6 pb-6",
      footer:
        "flex items-center justify-between p-4 border-t border-admin_neutral_1",
    },

    // Input Display
    input: {
      wrapper:
        "cursor-pointer p-4 bg-admin_paper rounded-lg border border-admin_neutral_1",
      label: "text-sm text-admin_text/60 mb-1",
      value: "text-sm text-admin_text",
    },

    // Header Elements
    header: {
      title: "text-admin_text text-base font-normal",
      closeButton: "w-6 h-6 text-admin_text/60 cursor-pointer",
      selectedDate: "text-[25px] text-admin_text font-normal mb-4 text-center",
      rangeRow: "flex items-stretch gap-3 mb-4",
      rangeSlot: "flex-1 min-w-0 rounded-xl px-3 py-2 border border-admin_neutral_1 text-left",
      rangeSlotActive: "border-admin_primary bg-admin_primary/5",
      rangeLabel: "text-[11px] uppercase tracking-wide text-admin_text/50",
      rangeValue: "text-[17px] leading-tight text-admin_text font-normal",
      monthRow: "flex items-center justify-center gap-1 mb-4",
      monthStep:
        "w-9 h-9 rounded-full hover:bg-admin_neutral_1/50 flex items-center justify-center text-admin_text",
      monthSelector:
        "text-admin_text hover:bg-admin_neutral_1/50 px-4 py-2 rounded-lg flex items-center",
    },

    // Calendar Grid
    calendar: {
      weekdayHeader: "text-center text-sm py-2 text-admin_text/60",
      daysGrid: "grid grid-cols-7",
      dayButton: {
        base: "relative aspect-square flex items-center justify-center text-sm text-admin_text rounded-full hover:bg-admin_primary",
        selected: "text-admin_text_inv bg-admin_primary",
        today: "font-bold text-admin_primary",
        otherMonth: "text-admin_text/40",
        inRange: "bg-admin_primary/20",
        dayWrapper: "w-10 h-10 flex items-center justify-center rounded-full",
      },
    },

    // Month/Year Selector
    monthSelector: {
      container: "bg-admin_paper rounded-[28px] p-6",
      header: {
        wrapper: "flex items-center justify-between mb-6",
        format: "text-admin_text/60",
        closeButton: "w-6 h-6 text-admin_text/60 cursor-pointer",
      },
      selectors: {
        wrapper: "flex gap-4 mb-6",
        button: {
          base: "w-full flex items-center justify-between gap-2 px-4 py-2 rounded-lg border border-admin_neutral_1",
          active: "bg-admin_neutral_1/50",
          hover: "hover:bg-admin_neutral_1/30",
          disabled: "text-admin_text/40",
        },
      },
      monthList: {
        wrapper: "space-y-2",
        item: {
          base: "w-full px-4 py-3 rounded-lg text-left flex items-center",
          selected: "bg-admin_primary/10",
          hover: "hover:bg-admin_neutral_1/50",
          checkmark: "w-5 h-5 text-admin_primary mr-2",
        },
      },
    },

    // Footer Buttons
    buttons: {
      base: "px-6 py-2 rounded-full",
      primary: "text-admin_primary hover:bg-admin_primary/10",
      secondary: "text-admin_primary hover:bg-admin_neutral_1/50",
    },
  },
}
